/**
 * `?boot-report` — the boot, in numbers, on the machine that ran it.
 *
 * ## Why this exists
 *
 * Every slow-boot report this port has had ("stuck at 99 % for almost a
 * minute", "sometimes 30-40 s", "preflight checks up to a minute") arrived as a
 * *percept*, and every one of them cost a lane a day of guessing because the
 * only measurements available were taken on a machine where the boot is two
 * seconds. `docs/engineering/lessons.md` states the rule that follows from
 * that: **get the capture from the session that is slow.** This is the capture,
 * in a form that is one screenshot — or, since the user asked for it on
 * 2026-09-01, one paste: `bootReportText` is the same string the panel shows,
 * and the panel's COPY button puts exactly it on the clipboard.
 *
 * It is deliberately **not** telemetry: nothing is uploaded, nothing is stored,
 * and nothing runs unless the URL asks for it. The instrument itself lives next
 * door in `boot-probe.ts` — it wraps a handful of `GPUDevice` methods, so an
 * ordinary boot must not pay for it, and does not, because `installBootProbe`
 * returns immediately when the parameter is absent. **This file is the
 * formatter**: what the numbers mean, and how they are laid out so a paste can
 * be read against `fly-boot-timing.md`'s tables column for column.
 *
 * ## What the numbers mean, and which one matters
 *
 * The phase table is the same `fly-boot:<phase>` marks `fly-boot-timing.md` is
 * written from. The line that decides everything is **`precompile`**, and it is
 * printed with its two factors rather than as a total: the pipeline count and
 * the mean `createRenderPipelineAsync` round trip. That phase costs
 * `count × roundTrip` (§6e), the count is a property of the scene and is the
 * same everywhere, and the round trip is a property of the driver — so a report
 * showing 99 pipelines at 6 ms is a healthy machine and one showing 99 at 600 ms
 * is a driver compiling two orders of magnitude slower, and those are different
 * problems with different fixes. Without both factors the number is just "slow".
 *
 * ## `gpu errors`, and why a zero there is worth a line of its own
 *
 * Everything above is about a boot that is *slow*. The 2026-09-01 "there are no
 * trees on London" report is the other kind — a boot that finished in four
 * seconds and drew the wrong picture — and it cost this lane the same guessing,
 * for the same reason: three r185 wraps every pipeline creation in a
 * `pushErrorScope('validation')` and answers a failure with a `console.error`
 * and a `pipelineData.error` flag (`WebGPUPipelineUtils.js`), so a pipeline that
 * WebKit refuses is a **silent missing mesh** to anyone who is not looking at
 * that user's console. `lessons.md` has the family: a `200` is not a delivery,
 * and neither is a render pass that ran.
 *
 * The probe therefore counts GPU errors from the two places they surface, and
 * says which is which in the first message it kept:
 *
 * - `[pipeline]` — three's own error scope came back non-null, or
 *   `createRenderPipelineAsync` rejected. A shader or pipeline the driver
 *   refused.
 * - `[uncaptured]` — the device's `uncapturederror`, which is everything
 *   *outside* three's scopes: a bad buffer binding, a vertex layout the backend
 *   would not take, a draw the validator killed. The line is called `gpu errors`
 *   rather than `pipeline errors` precisely so this case cannot be read as a
 *   compile failure.
 *
 * The listener is attached with `addEventListener`, never by assigning
 * `device.onuncapturederror`, because three assigns that property itself
 * (`WebGPUBackend.js`) and a probe that silences the renderer's own error path
 * would be worse than no probe.
 *
 * `RENDERER` is the second thing worth a glance: the WebGL2 fallback compiles
 * its shaders on a path of its own, and measured on this project's reference
 * machine the same scene's `precompile` is **1,958 ms on WebGL against 713 ms on
 * WebGPU** with the serial compile — so a report that says `WEBGL` has already
 * explained a good part of its own slowness.
 */

/** The URL parameter. Hyphenated to read as prose in a pasted URL, like `?boot-report`. */
export const BOOT_REPORT_PARAM = "boot-report";

/** Whether this document was asked for a report. Pure, so the decision is testable with no `location`. */
export function bootReportWanted(search: string | undefined): boolean {
  if (!search) return false;
  return new URLSearchParams(search).has(BOOT_REPORT_PARAM);
}

/**
 * What the probe counted.
 *
 * **Two per-pipeline numbers, because one of them stops meaning what it says
 * the moment the compiles overlap.** `roundTripMs` is the wall time from a
 * `createRenderPipelineAsync` call to its promise; under the serial loop that
 * is the driver's own latency, but under the concurrent one every call is in
 * flight at once and each "round trip" reads as almost the whole phase (99
 * pipelines, 351 ms of phase, 77 ms of mean round trip — measured on this
 * project's reference machine, and the three numbers are consistent, not
 * contradictory). `perPipelineMs` is the phase divided by the pipelines
 * compiled inside it, which is the amortised cost and is comparable across
 * modes and machines. The report prints both and says which is which.
 */
export interface PipelineProbe {
  readonly pipelines: number;
  readonly asyncCalls: number;
  readonly roundTripMs: number;
  readonly maxRoundTripMs: number;
  /** Pipelines created between the `precompile` mark and `ready`, or the whole count when the marks are missing. */
  readonly inPrecompile: number;
  /** GPU errors seen this session, from three's own pipeline error scopes and from the device's `uncapturederror`. */
  readonly gpuErrors: number;
  /** The first one, already tagged `[pipeline]` or `[uncaptured]` and truncated — or `""` when there were none. */
  readonly firstGpuError: string;
}

/**
 * The `GPUAdapterInfo` fields as one readable line, with repeated words dropped.
 *
 * `vendor`/`architecture`/`device`/`description` are four independent fields
 * that a browser is free to fill with the same word, and Safari 26.5 on an
 * Apple GPU fills all four with `apple` — so the 2026-09-01 report came back
 * reading `adapter          apple apple apple apple`, which says nothing four
 * times. Chromium fills two of them (`nvidia`, `blackwell`) and is unaffected
 * by the dedupe, which is why this is a *token* filter and not a "print fewer
 * fields" rule: which fields carry the information is the browser's choice, and
 * dropping one by name would lose the vendor on a machine that only fills
 * `description`.
 *
 * Order is first-appearance, so the vendor still leads.
 */
export function adapterLabel(info: {
  readonly vendor?: string;
  readonly architecture?: string;
  readonly device?: string;
  readonly description?: string;
}): string {
  const seen = new Set<string>();
  const kept: string[] = [];
  for (const field of [info.vendor, info.architecture, info.device, info.description]) {
    for (const token of (field ?? "").split(/\s+/)) {
      const key = token.toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      kept.push(token);
    }
  }
  return kept.join(" ");
}

/** One phase of the boot, as the report prints it. */
export interface BootReportPhase {
  readonly name: string;
  readonly startedMs: number;
  readonly durationMs: number;
}

/**
 * The `fly-boot:*` marks turned into durations — each phase lasting until the
 * next mark, the last one until `ready`.
 *
 * `boot-reporter.ts` marks a phase **once**, at its start, so a duration here is
 * a difference between neighbours and never a `performance.measure`. Marks that
 * are not phases (`renderer-webgpu`, `viewport-ok`) are dropped by the caller's
 * order list rather than filtered by name, so a new mark cannot silently become
 * a phase with a made-up duration.
 */
export function bootPhaseTable(marks: ReadonlyMap<string, number>, order: readonly string[]): readonly BootReportPhase[] {
  const present = order.filter((name) => marks.has(name));
  const ready = marks.get("ready");
  return present.map((name, index) => {
    const startedMs = marks.get(name) ?? 0;
    const next = present[index + 1];
    const endsMs = (next === undefined ? ready : marks.get(next)) ?? startedMs;
    return { name, startedMs, durationMs: endsMs - startedMs };
  });
}

/** Reads the document's own `fly-boot:` marks into a map, so the table above needs no `performance`. */
export function bootMarks(): Map<string, number> {
  const marks = new Map<string, number>();
  if (typeof performance === "undefined") return marks;
  for (const entry of performance.getEntriesByType("mark")) {
    if (entry.name.startsWith("fly-boot:")) marks.set(entry.name.slice("fly-boot:".length), entry.startTime);
  }
  return marks;
}

/** The whole report as plain lines, ready to be dropped into a `<pre>` and screenshotted. */
export function bootReportLines(input: {
  readonly marks: ReadonlyMap<string, number>;
  readonly order: readonly string[];
  readonly probe: PipelineProbe;
  readonly renderer: string;
  readonly adapter: string;
  readonly userAgent: string;
}): readonly string[] {
  const ready = input.marks.get("ready") ?? 0;
  const phases = bootPhaseTable(input.marks, input.order);
  const precompileMs = phases.find((phase) => phase.name === "precompile")?.durationMs ?? 0;
  const compiled = input.probe.inPrecompile;
  const lines = [`RED BARON — BOOT REPORT`, `total to ready   ${Math.round(ready)} ms`, `${input.renderer}`];
  if (input.adapter) lines.push(`adapter          ${input.adapter}`);
  lines.push("", "phase            start      ms");
  for (const phase of phases) {
    lines.push(`${phase.name.padEnd(16)} ${String(Math.round(phase.startedMs)).padStart(6)}  ${String(Math.round(phase.durationMs)).padStart(6)}`);
  }
  lines.push("");
  if (input.probe.pipelines === 0) {
    // Zero is the probe being honest, not the backend compiling nothing: it
    // wraps `GPUDevice`, which a WebGL2 boot never touches. Saying so is worth
    // three lines, because "0 pipelines" in a screenshot is a question.
    lines.push(`pipelines        not counted — the probe wraps GPUDevice, and this boot did not use one`);
  } else {
    lines.push(
      `pipelines        ${compiled} in precompile, ${input.probe.pipelines} total (${input.probe.asyncCalls} async)`,
      `per pipeline     ${compiled > 0 ? (precompileMs / compiled).toFixed(2) : "—"} ms  (the phase divided by the count — compare this one)`,
      `round trip       mean ${input.probe.roundTripMs.toFixed(1)} ms, max ${input.probe.maxRoundTripMs.toFixed(1)} ms  (overlapping, so ≫ per pipeline)`,
    );
  }
  // Printed on every WebGPU boot, `none` included: a report that omits the line
  // when it is zero cannot be told apart from a report taken before this
  // existed, and "no errors" is the single most useful thing a missing-scenery
  // report can carry.
  if (input.probe.pipelines > 0 || input.probe.gpuErrors > 0) {
    lines.push(
      input.probe.gpuErrors === 0
        ? `gpu errors       none`
        : `gpu errors       ${input.probe.gpuErrors}  first: ${input.probe.firstGpuError}`,
    );
  }
  lines.push("", input.userAgent);
  return lines;
}

/**
 * The report as one block of plain text.
 *
 * The panel's `<pre>` and the clipboard both go through here, so the string a
 * user pastes into a bug report is the string he was looking at when he pressed
 * the button — character for character, not a second formatter that drifts.
 * No trailing newline: a `<pre>` renders one as an extra blank line inside the
 * box, and the paste reads the same without it.
 */
export function bootReportText(lines: readonly string[]): string {
  return lines.join("\n");
}

/** What the copy button has to say for itself. `failed` is a state and not a shrug — see `copyBootReport`. */
export type BootReportCopyState = "idle" | "copied" | "failed";

/** The clipboard as this module needs it: `navigator.clipboard.writeText`, or nothing where there is none. */
export type ClipboardWriter = (text: string) => Promise<void>;

/**
 * Puts the report on the clipboard and says honestly whether it landed.
 *
 * `navigator.clipboard` is absent outside a secure context and `writeText`
 * rejects when the document has no permission or is not focused, so both a
 * missing writer and a rejected write are *failures the user must see*: a
 * button that flashed COPIED over an empty clipboard would send the next
 * slow-boot report as another screenshot-less "it was slow". The writer is a
 * parameter rather than a `navigator` read so both paths are testable without
 * a browser.
 */
export async function copyBootReport(text: string, write: ClipboardWriter | undefined): Promise<BootReportCopyState> {
  if (!write) {
    console.warn("boot report: no clipboard on this document — copying needs a secure context");
    return "failed";
  }
  try {
    await write(text);
    return "copied";
  } catch (error) {
    // The button has room for three words and the reason is worth more than
    // that: a `NotAllowedError` from an unfocused document and a permission
    // the user denied are different fixes, and this panel's whole job is to
    // put the diagnosis where someone can read it.
    console.warn("boot report: the clipboard refused the write", error);
    return "failed";
  }
}

/** The button's face for each state, in the panel's own shouted-monospace register. */
export function bootReportCopyLabel(state: BootReportCopyState): string {
  return state === "copied" ? "COPIED" : state === "failed" ? "COPY FAILED" : "COPY";
}
